import React, { useState } from 'react';
import { Facebook, Image } from 'lucide-react';
import { FacebookAdsContent } from '../types/ads.types';
import { AD_PLATFORMS } from '../config/adPlatformConfig';
import MediaUploader from '../components/MediaUploader';
import VariationToggle from '../components/VariationToggle';
import PlatformEditorTabs from '../components/PlatformEditorTabs';
import AssetSubTabs from '../components/AssetSubTabs';

interface BlobUrlManager {
  add: (url: string) => void;
  remove: (url: string) => void;
  cleanup: () => void;
}

interface FacebookAdsEditorProps {
  content: FacebookAdsContent;
  isEditing: boolean;
  onChange: (field: keyof FacebookAdsContent, value: any) => void;
  blobManager?: BlobUrlManager;
  contentId?: number;
  apiBaseUrl?: string;
}

export default function FacebookAdsEditor({ content, isEditing, onChange, blobManager, contentId, apiBaseUrl }: FacebookAdsEditorProps) {
  const platform = AD_PLATFORMS.facebook;
  const [currentPrimaryTextIndex, setCurrentPrimaryTextIndex] = useState(0);
  const [currentHeadlineIndex, setCurrentHeadlineIndex] = useState(0);
  const [currentDescriptionIndex, setCurrentDescriptionIndex] = useState(0);
  const [showAllPrimaryText, setShowAllPrimaryText] = useState(false);
  const [showAllHeadlines, setShowAllHeadlines] = useState(false);
  const [showAllDescriptions, setShowAllDescriptions] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [activeAssetSubTab, setActiveAssetSubTab] = useState('text');

  const tabs = [
    { id: 'settings', label: 'Settings' },
    { id: 'assets', label: 'Assets' },
    { id: 'targeting', label: 'Targeting' },
    { id: 'budget', label: 'Budget' }
  ];

  const assetSubTabs = [
    { id: 'text', label: 'Text' },
    { id: 'images', label: 'Images' },
    { id: 'videos', label: 'Videos' },
    { id: 'carousel', label: 'Carousel' }
  ];

  // Initialize variations if they don't exist
  const primaryTextVariations = content.primaryTextVariations || [content.primaryText || ''];
  const headlineVariations = content.headlineVariations || [content.headline || ''];
  const descriptionVariations = content.descriptionVariations || [content.description || ''];

  const updatePrimaryTextVariations = (variations: string[]) => {
    onChange('primaryTextVariations', variations);
    // Keep main field in sync for backwards compatibility
    onChange('primaryText', variations[currentPrimaryTextIndex] || '');
  };

  const updateHeadlineVariations = (variations: string[]) => {
    onChange('headlineVariations', variations);
    // Keep main field in sync for backwards compatibility
    onChange('headline', variations[currentHeadlineIndex] || '');
  };

  const updateDescriptionVariations = (variations: string[]) => {
    onChange('descriptionVariations', variations);
    // Keep main field in sync for backwards compatibility
    onChange('description', variations[currentDescriptionIndex] || '');
  };

  return (
    <div className="space-y-6">
      <PlatformEditorTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'settings' && (
        <>
          <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Facebook className="w-5 h-5 mr-2 text-blue-600" />
          Facebook & Instagram Campaign
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
            <input
              type="text"
              value={content.campaignName || ''}
              onChange={(e) => onChange('campaignName', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ad Format</label>
            <select
              value={content.adFormat || 'image'}
              onChange={(e) => onChange('adFormat', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              {platform.formats.map((format) => (
                <option key={format.toLowerCase().replace(' ', '_')} value={format.toLowerCase().replace(' ', '_')}>
                  {format}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Objective</label>
            <select
              value={content.objective || 'traffic'}
              onChange={(e) => onChange('objective', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="awareness">Brand Awareness</option>
              <option value="traffic">Traffic</option>
              <option value="engagement">Engagement</option>
              <option value="leads">Lead Generation</option>
              <option value="sales">Conversions</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Call to Action</label>
            <select
              value={content.callToAction || 'learn_more'}
              onChange={(e) => onChange('callToAction', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="shop_now">Shop Now</option>
              <option value="learn_more">Learn More</option>
              <option value="sign_up">Sign Up</option>
              <option value="download">Download</option>
              <option value="contact_us">Contact Us</option>
            </select>
          </div>
        </div>
      </div>
        </>
      )}

      {activeTab === 'assets' && (
        <>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <AssetSubTabs
              tabs={assetSubTabs}
              activeTab={activeAssetSubTab}
              onTabChange={setActiveAssetSubTab}
            />

            {activeAssetSubTab === 'text' && (
              <div className="space-y-6">
                {/* Move primary text, headline, description fields here from settings */}
                <div>
                  <VariationToggle
                    variations={primaryTextVariations}
                    label={`Primary Text Variations (${primaryTextVariations.length})`}
                    currentIndex={currentPrimaryTextIndex}
                    onIndexChange={setCurrentPrimaryTextIndex}
                    isEditing={isEditing}
                    maxCharLimit={125}
                    showAllVariations={showAllPrimaryText}
                    onToggleShowAll={() => setShowAllPrimaryText(!showAllPrimaryText)}
                  />
                  {isEditing && (
                    <div className="mt-2">
                      <textarea
                        value={primaryTextVariations[currentPrimaryTextIndex] || ''}
                        onChange={(e) => {
                          const newVariations = [...primaryTextVariations];
                          newVariations[currentPrimaryTextIndex] = e.target.value;
                          updatePrimaryTextVariations(newVariations);
                        }}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        maxLength={125}
                        rows={3}
                        placeholder="Primary text for your ad..."
                      />
                      <div className="flex justify-between mt-2">
                        <button
                          onClick={() => {
                            const newVariations = [...primaryTextVariations, ''];
                            updatePrimaryTextVariations(newVariations);
                            setCurrentPrimaryTextIndex(newVariations.length - 1);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          + Add Primary Text
                        </button>
                        {primaryTextVariations.length > 1 && (
                          <button
                            onClick={() => {
                              const newVariations = primaryTextVariations.filter((_, i) => i !== currentPrimaryTextIndex);
                              updatePrimaryTextVariations(newVariations);
                              setCurrentPrimaryTextIndex(Math.max(0, currentPrimaryTextIndex - 1));
                            }}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Remove Current
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <VariationToggle
                    variations={headlineVariations}
                    label={`Headlines (${headlineVariations.length})`}
                    currentIndex={currentHeadlineIndex}
                    onIndexChange={setCurrentHeadlineIndex}
                    isEditing={isEditing}
                    maxCharLimit={40}
                    showAllVariations={showAllHeadlines}
                    onToggleShowAll={() => setShowAllHeadlines(!showAllHeadlines)}
                  />
                  {isEditing && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={headlineVariations[currentHeadlineIndex] || ''}
                        onChange={(e) => {
                          const newVariations = [...headlineVariations];
                          newVariations[currentHeadlineIndex] = e.target.value;
                          updateHeadlineVariations(newVariations);
                        }}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        maxLength={40}
                        placeholder="Headline"
                      />
                      <div className="flex justify-between mt-2">
                        <button
                          onClick={() => {
                            const newVariations = [...headlineVariations, ''];
                            updateHeadlineVariations(newVariations);
                            setCurrentHeadlineIndex(newVariations.length - 1);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          + Add Headline
                        </button>
                        {headlineVariations.length > 1 && (
                          <button
                            onClick={() => {
                              const newVariations = headlineVariations.filter((_, i) => i !== currentHeadlineIndex);
                              updateHeadlineVariations(newVariations);
                              setCurrentHeadlineIndex(Math.max(0, currentHeadlineIndex - 1));
                            }}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Remove Current
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <VariationToggle
                    variations={descriptionVariations}
                    label={`Descriptions (${descriptionVariations.length})`}
                    currentIndex={currentDescriptionIndex}
                    onIndexChange={setCurrentDescriptionIndex}
                    isEditing={isEditing}
                    maxCharLimit={30}
                    showAllVariations={showAllDescriptions}
                    onToggleShowAll={() => setShowAllDescriptions(!showAllDescriptions)}
                  />
                  {isEditing && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={descriptionVariations[currentDescriptionIndex] || ''}
                        onChange={(e) => {
                          const newVariations = [...descriptionVariations];
                          newVariations[currentDescriptionIndex] = e.target.value;
                          updateDescriptionVariations(newVariations);
                        }}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        maxLength={30}
                        placeholder="Optional description"
                      />
                      <div className="flex justify-between mt-2">
                        <button
                          onClick={() => {
                            const newVariations = [...descriptionVariations, ''];
                            updateDescriptionVariations(newVariations);
                            setCurrentDescriptionIndex(newVariations.length - 1);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          + Add Description
                        </button>
                        {descriptionVariations.length > 1 && (
                          <button
                            onClick={() => {
                              const newVariations = descriptionVariations.filter((_, i) => i !== currentDescriptionIndex);
                              updateDescriptionVariations(newVariations);
                              setCurrentDescriptionIndex(Math.max(0, currentDescriptionIndex - 1));
                            }}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Remove Current
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeAssetSubTab === 'images' && (
              <div>
                <MediaUploader
                  creatives={content.creatives?.filter(c => c.type === 'image') || []}
                  onCreativesChange={(creatives) => {
                    const otherCreatives = content.creatives?.filter(c => c.type !== 'image') || [];
                    onChange('creatives', [...otherCreatives, ...creatives]);
                  }}
                  disabled={!isEditing}
                  platform="Facebook & Instagram - Images"
                  acceptedTypes={['image/*']}
                  maxFiles={10}
                  blobManager={blobManager}
                  contentId={contentId}
                  apiBaseUrl={apiBaseUrl}
                />
              </div>
            )}

            {activeAssetSubTab === 'videos' && (
              <div>
                <MediaUploader
                  creatives={content.creatives?.filter(c => c.type === 'video') || []}
                  onCreativesChange={(creatives) => {
                    const otherCreatives = content.creatives?.filter(c => c.type !== 'video') || [];
                    onChange('creatives', [...otherCreatives, ...creatives]);
                  }}
                  disabled={!isEditing}
                  platform="Facebook & Instagram - Videos"
                  acceptedTypes={['video/*']}
                  maxFiles={5}
                  blobManager={blobManager}
                  contentId={contentId}
                  apiBaseUrl={apiBaseUrl}
                />
              </div>
            )}

            {activeAssetSubTab === 'carousel' && (
              <div>
                <p className="text-sm text-gray-600 mb-4">Upload multiple images for carousel ads (2-10 cards)</p>
                <MediaUploader
                  creatives={content.carouselCards || []}
                  onCreativesChange={(cards) => onChange('carouselCards', cards)}
                  disabled={!isEditing}
                  platform="Facebook Carousel"
                  acceptedTypes={['image/*']}
                  maxFiles={10}
                  minFiles={2}
                  blobManager={blobManager}
                  contentId={contentId}
                  apiBaseUrl={apiBaseUrl}
                />
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'targeting' && (
        <>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4">Targeting</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Locations</label>
                <input
                  type="text"
                  value={content.locations || ''}
                  onChange={(e) => onChange('locations', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  placeholder="United States, Canada..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age Range</label>
                <input
                  type="text"
                  value={content.ageRange || ''}
                  onChange={(e) => onChange('ageRange', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  placeholder="18-65+"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Interests</label>
                <textarea
                  value={content.interests || ''}
                  onChange={(e) => onChange('interests', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 h-20"
                  placeholder="Technology, Business, Marketing..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Audiences</label>
                <textarea
                  value={content.customAudiences || ''}
                  onChange={(e) => onChange('customAudiences', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 h-16"
                  placeholder="Lookalike audiences, customer lists..."
                />
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'budget' && (
        <>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4">Budget & Schedule</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget Amount</label>
                  <input
                    type="number"
                    value={content.budget || ''}
                    onChange={(e) => onChange('budget', parseFloat(e.target.value))}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    placeholder="100.00"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget Type</label>
                  <select
                    value={content.budgetType || 'daily'}
                    onChange={(e) => onChange('budgetType', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  >
                    <option value="daily">Daily</option>
                    <option value="lifetime">Lifetime</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Objective</label>
                <select
                  value={content.objective || 'conversions'}
                  onChange={(e) => onChange('objective', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="awareness">Brand Awareness</option>
                  <option value="reach">Reach</option>
                  <option value="traffic">Traffic</option>
                  <option value="engagement">Engagement</option>
                  <option value="conversions">Conversions</option>
                  <option value="catalog_sales">Catalog Sales</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={content.startDate || ''}
                    onChange={(e) => onChange('startDate', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={content.endDate || ''}
                    onChange={(e) => onChange('endDate', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}